import React, { useState } from 'react';
import { Question, UserResponse, TestVariant } from '../types';
import { getUserId, saveResponse } from '../utils/storage';

interface SurveyFormProps {
  questions: Question[];
  variant: TestVariant;
  onComplete: () => void;
}

export default function SurveyForm({ questions, variant, onComplete }: SurveyFormProps) {
  const [answers, setAnswers] = useState<Record<string, string | number>>({});
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (questionId: string, value: string | number) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const userId = getUserId();
      
      const response: UserResponse = {
        userId,
        variantId: variant.id,
        timestamp: Date.now(),
        answers: Object.keys(answers).map(questionId => ({
          questionId,
          answer: answers[questionId]
        }))
      };
      
      saveResponse(response);
      
      // Simulate network delay for better UX
      setTimeout(() => {
        setIsSubmitting(false);
        onComplete();
      }, 1000);
    } catch (error) {
      setIsSubmitting(false);
      console.error('Error submitting response:', error);
      alert('There was an error submitting your response. Please try again.');
    }
  };

  const currentQuestion = questions[currentStep];
  const isLastQuestion = currentStep === questions.length - 1;
  const hasAnswer = currentQuestion && answers[currentQuestion.id] !== undefined;

  const renderQuestionInput = (question: Question) => {
    switch (question.type) {
      case 'text':
        return (
          <textarea
            id={question.id}
            value={answers[question.id] as string || ''}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none h-32"
            placeholder="Type your answer here..."
          />
        );
      
      case 'likert':
        return (
          <div className="flex flex-col sm:flex-row justify-between items-center w-full mt-4 space-y-2 sm:space-y-0">
            <span className="text-sm text-gray-500">Strongly Disagree</span>
            <div className="flex space-x-4">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleInputChange(question.id, value)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    answers[question.id] === value
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  } transition-colors duration-200`}
                >
                  {value}
                </button>
              ))}
            </div>
            <span className="text-sm text-gray-500">Strongly Agree</span>
          </div>
        );
      
      case 'multipleChoice':
        return (
          <div className="flex flex-col space-y-2 mt-4">
            {question.options?.map((option, index) => (
              <label
                key={index}
                className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors duration-200 ${
                  answers[question.id] === option
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name={question.id}
                  value={option}
                  checked={answers[question.id] === option}
                  onChange={() => handleInputChange(question.id, option)}
                  className="hidden"
                />
                <div className={`w-5 h-5 rounded-full border mr-3 flex items-center justify-center ${
                  answers[question.id] === option
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-400'
                }`}>
                  {answers[question.id] === option && (
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                  )}
                </div>
                <span>{option}</span>
              </label>
            ))}
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-xl font-semibold text-gray-800">
            {currentStep + 1}. {currentQuestion?.text}
          </h2>
          <span className="text-sm text-gray-500">
            Question {currentStep + 1} of {questions.length}
          </span>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-1.5 mb-6">
          <div
            className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
          ></div>
        </div>
      </div>

      <div className="mb-8">
        {currentQuestion && renderQuestionInput(currentQuestion)}
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={handleBack}
          disabled={currentStep === 0}
          className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
            currentStep === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Back
        </button>
        
        <button
          type="button"
          onClick={handleNext}
          disabled={!hasAnswer || isSubmitting}
          className={`px-6 py-2 rounded-lg transition-colors duration-200 ${
            !hasAnswer || isSubmitting
              ? 'bg-blue-300 cursor-not-allowed'
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          }`}
        >
          {isSubmitting ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white\" xmlns="http://www.w3.org/2000/svg\" fill="none\" viewBox="0 0 24 24">
                <circle className="opacity-25\" cx="12\" cy="12\" r="10\" stroke="currentColor\" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Submitting...
            </span>
          ) : (
            isLastQuestion ? 'Submit' : 'Next'
          )}
        </button>
      </div>
    </div>
  );
}